import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AutomationAppUIModel } from '@/types/automation';
import { normalizeLocation } from '@/utils/system';

type Theme = 'light' | 'dark';
export type ReduceMotionPreference = 'system' | 'always';

/**
 * Sentinel for "no pinned default model" — let the assistant backend pick its
 * own default from the live model catalog. The Settings dropdown surfaces this
 * as "Automatic"; any other value is a concrete model id chosen from the
 * backend's `listModels` catalog (see `useModelCatalog`).
 */
export const ASSISTANT_MODEL_AUTOMATIC = '';

type AppState = {
  selectedAppName: string | null;
  selectedApp: AutomationAppUIModel | null;
  theme: Theme;
  selectedTenantId: string | null;
  /** Selected location (Azure region) or null for "All" */
  selectedLocation: string | null;
  /** Selected project (resource group) or null for "All" */
  selectedProject: string | null;

  // ── User preferences (Settings modal) ──────────────────────────────────
  // NOTE: default canvas orientation lives in `workflowViewPrefsStore` —
  // it's the source of truth for both the workspace default and per-workflow
  // overrides. Settings → Appearance reads/writes that store directly.
  /** 'system' honors prefers-reduced-motion; 'always' forces it. */
  reduceMotion: ReduceMotionPreference;
  /** Send anonymous usage telemetry. */
  telemetryEnabled: boolean;
  /** Send crash reports (stack traces + breadcrumbs). */
  crashReportsEnabled: boolean;
  /** Default model for the workflow assistant. Empty string = Automatic
   *  (let the backend choose from its live catalog). Client-only for now. */
  assistantDefaultModel: string;
  /**
   * Default agent-mode for new Copilot chat sessions.
   * `true`  → new chats start in agent mode (agentic workflows, agent triggers).
   * `false` → new chats start in classic mode (traditional workflows).
   *
   * The in-chat toggle (bot icon in the composer) still wins per session;
   * this preference only controls the initial value the chat store boots
   * with after a page load.
   */
  assistantDefaultAgentMode: boolean;
};

type AppActions = {
  setSelectedApp: (app: AutomationAppUIModel) => void;
  setSelectedAppName: (appName: string | null) => void;
  clearSelectedApp: () => void;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  setSelectedTenant: (tenantId: string | null) => void;
  setSelectedLocation: (location: string | null) => void;
  setProjectName: (project: string | null) => void;
  // Settings modal actions
  setReduceMotion: (pref: ReduceMotionPreference) => void;
  setTelemetryEnabled: (enabled: boolean) => void;
  setCrashReportsEnabled: (enabled: boolean) => void;
  setAssistantDefaultModel: (model: string) => void;
  setAssistantDefaultAgentMode: (enabled: boolean) => void;
  /**
   * Non-destructive bridge for new hierarchical routes.
   * Syncs URL-derived context into the store without clearing unrelated fields.
   * Used by ProjectLayout/AppLayout instead of setProjectName/setSelectedApp
   * to avoid the destructive clears those actions perform.
   */
  hydrateRouteContext: (ctx: { projectName?: string; app?: AutomationAppUIModel; locationName?: string }) => void;
};

type AppStore = AppState & AppActions;

export const useAppStore = create<AppStore>()(
  persist(
    (set) => ({
      selectedAppName: null,
      selectedApp: null,
      theme: 'dark',
      selectedTenantId: null,
      selectedLocation: null,
      selectedProject: null,
      reduceMotion: 'system',
      telemetryEnabled: true,
      crashReportsEnabled: true,
      assistantDefaultModel: ASSISTANT_MODEL_AUTOMATIC,
      assistantDefaultAgentMode: true,

      setSelectedApp: (app) =>
        set({
          selectedApp: app,
          selectedAppName: app.name,
        }),

      setSelectedAppName: (appName) =>
        set({
          selectedAppName: appName,
          selectedApp: null,
        }),

      clearSelectedApp: () =>
        set({
          selectedApp: null,
          selectedAppName: null,
        }),

      setTheme: (theme) => set({ theme }),

      toggleTheme: () =>
        set((state) => ({
          theme: state.theme === 'dark' ? 'light' : 'dark',
        })),

      setSelectedLocation: (location) =>
        set({
          selectedLocation: normalizeLocation(location),
          selectedAppName: null,
          selectedApp: null,
        }),

      setSelectedTenant: (tenantId) =>
        set({
          selectedTenantId: tenantId,
          selectedAppName: null,
          selectedApp: null,
        }),

      setProjectName: (project) =>
        set({
          selectedProject: project,
          selectedApp: null,
          selectedAppName: null,
        }),

      setReduceMotion: (pref) => set({ reduceMotion: pref }),
      setTelemetryEnabled: (enabled) => set({ telemetryEnabled: enabled }),
      setCrashReportsEnabled: (enabled) => set({ crashReportsEnabled: enabled }),
      setAssistantDefaultModel: (model) => set({ assistantDefaultModel: model }),
      setAssistantDefaultAgentMode: (enabled) => set({ assistantDefaultAgentMode: enabled }),

      hydrateRouteContext: ({ projectName, app, locationName }) =>
        set((state) => {
          const next: Partial<AppState> = {};

          if (projectName !== undefined && projectName !== state.selectedProject) {
            next.selectedProject = projectName;
            // Clear stale app state when project changes and no app is provided,
            // preventing project-B + app-from-project-A inconsistency.
            if (app === undefined) {
              next.selectedApp = null;
              next.selectedAppName = null;
            }
          }

          if (app !== undefined && app.name !== state.selectedApp?.name) {
            next.selectedApp = app;
            next.selectedAppName = app.name;
          }

          // Mirror the route's location segment into the store so consumers
          // that read selectedLocation (CreateAppDialog, useRepoOAuthConnection,
          // etc.) see the actual region of the route the user is on, not
          // whatever the dashboard's last filter happened to be. Non-destructive
          // unlike `setSelectedLocation` — we do NOT clear the selected app,
          // since the caller is hydrating from a route that already pins the app.
          if (locationName !== undefined) {
            const normalized = normalizeLocation(locationName);
            if (normalized && normalized !== state.selectedLocation) {
              next.selectedLocation = normalized;
            }
          }

          return Object.keys(next).length > 0 ? next : state;
        }),
    }),
    {
      name: 'otto-app-store',
      version: 2,
      migrate: (persisted, version) => {
        const state = persisted as Partial<AppState> | undefined;
        if (!state) return state;
        let next: Partial<AppState> = state;
        if (version < 1) {
          next = { ...next, selectedLocation: normalizeLocation(next.selectedLocation) };
        }
        if (version < 2) {
          // v2 introduces user-preference fields. Only fill keys that are
          // missing or undefined in the persisted snapshot so explicit
          // undefined values from older builds can't clobber the defaults.
          next = {
            ...next,
            reduceMotion: next.reduceMotion ?? 'system',
            telemetryEnabled: next.telemetryEnabled ?? true,
            crashReportsEnabled: next.crashReportsEnabled ?? true,
            assistantDefaultModel: next.assistantDefaultModel ?? ASSISTANT_MODEL_AUTOMATIC,
            assistantDefaultAgentMode: next.assistantDefaultAgentMode ?? true,
          };
        }
        // Legacy value cleanup: early builds shipped a 'never' option for
        // reduce-motion that's no longer in the type union. Coerce stored
        // 'never' values back to 'system' so the toggle reflects the actual
        // observed behavior (no CSS rule was ever emitted for 'never').
        // Runs on every load — no schema bump because the persisted snapshot
        // is still version 2.
        if ((next.reduceMotion as unknown) === 'never') {
          next = { ...next, reduceMotion: 'system' };
        }
        // v2-era stores carried `defaultCanvasOrientation` (ownership moved
        // to `workflowViewPrefsStore.defaults.orientation`) and the original
        // assistantReasoningEffort / assistantGlobalInstructions preferences
        // that were dropped before ship because the assistant backend has
        // no contract for them. Strip them on hydration so they stop
        // showing up in persisted snapshots.
        const dead = next as {
          defaultCanvasOrientation?: unknown;
          assistantReasoningEffort?: unknown;
          assistantGlobalInstructions?: unknown;
        };
        delete dead.defaultCanvasOrientation;
        delete dead.assistantReasoningEffort;
        delete dead.assistantGlobalInstructions;
        // Backfill the new assistantDefaultAgentMode field on already-migrated
        // v2 snapshots that pre-date its introduction.
        if (next.assistantDefaultAgentMode === undefined) {
          next = { ...next, assistantDefaultAgentMode: true };
        }
        return next;
      },
      partialize: (state) => ({
        selectedAppName: state.selectedAppName,
        selectedApp: state.selectedApp,
        selectedTenantId: state.selectedTenantId,
        selectedLocation: state.selectedLocation,
        selectedProject: state.selectedProject,
        theme: state.theme,
        reduceMotion: state.reduceMotion,
        telemetryEnabled: state.telemetryEnabled,
        crashReportsEnabled: state.crashReportsEnabled,
        assistantDefaultModel: state.assistantDefaultModel,
        assistantDefaultAgentMode: state.assistantDefaultAgentMode,
      }),
    }
  )
);
